import { useMemo } from "react";
import {
  Bar,
  ComposedChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { blendedRates, toDailyCosts } from "@/lib/cost/burnRate";
import type { TokenCostItem } from "@/hooks/useTokenCost";

interface ExecTrendChartProps {
  items: TokenCostItem[];
}

export function ExecTrendChart({ items }: ExecTrendChartProps) {
  const { t } = useTranslation();

  const dailyCosts = useMemo(() => {
    const modelItems = items.filter((i) => i.model !== null);
    const dateItems = items.filter((i) => i.date !== null);
    const rates = blendedRates(modelItems);
    if (!rates) return [];
    return toDailyCosts(dateItems, rates);
  }, [items]);

  if (dailyCosts.length === 0) return null;

  const data = dailyCosts.map((d) => ({
    date: d.date.slice(5),
    cost: Math.round(d.costUsd * 100) / 100,
  }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t("executive.family.cost")} — {t("executive.kpi.avgDaily7d")}
        </CardTitle>
      </CardHeader>
      <CardContent className="min-w-0">
        <ResponsiveContainer width="100%" height={160}>
          <ComposedChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => `$${v}`}
              width={36}
            />
            <Tooltip
              formatter={(v: number) => [`$${v.toFixed(2)}`, "USD"]}
              contentStyle={{ fontSize: 12 }}
            />
            <Bar
              dataKey="cost"
              fill="hsl(30 72% 47%)"
              radius={[2, 2, 0, 0]}
              maxBarSize={24}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
